import Button from 'atoms/Button';
import Heading from 'atoms/Heading';
import Text from 'atoms/Text';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { stripTenorSpace } from '@/helper/irs-clob-grid.helper';
import ApprovalGrid from '../ApprovalGrid';

const DASH = '-';

// Buyer/Seller read "<trader> · <company>", with the company carrying the grid's
// counterparty colour — ApprovalGrid paints `unit`, so the company travels there.
const partyRow = (label, party) => ({
    label,
    value: party?.name ? `${party.name} ·` : DASH,
    unit: party?.name ? party.company : undefined
});

const rowsFor = (hit) => [
    { label: 'Start:', value: stripTenorSpace(hit.start) || DASH },
    { label: 'Tenor:', value: stripTenorSpace(hit.tenor) || DASH },
    { label: 'Price:', value: hit.price ?? DASH, valueClass: 'text-primary-100' },
    {
        label: 'Size:',
        value: hit.size === null || hit.size === undefined
            ? DASH
            : <>{hit.size}<span className="text-warning-200">K</span></>
    },
    partyRow('Buyer Name', hit.buyer),
    partyRow('Seller Name', hit.seller)
];

/**
 * The bottom-left card telling an IDB broker their resting price was just hit or
 * lifted. Hits queue rather than stack: the pager walks them one at a time and
 * Dismiss drops the one on screen.
 */
const BrokerPriceHitDialog = ({ hits = [], onDismiss }) => {
    const [index, setIndex] = useState(0);

    // Dismissing the last card in the queue leaves the index past the end, so it
    // follows the list down rather than blanking the card.
    useEffect(() => {
        setIndex((current) => Math.max(0, Math.min(current, hits.length - 1)));
    }, [hits.length]);

    if (!hits.length) return null;

    const hit = hits[Math.min(index, hits.length - 1)];
    if (!hit) return null;

    const step = (delta) => setIndex((current) => (current + delta + hits.length) % hits.length);

    return (
        <div className="fixed bottom-5 left-5 z-50 w-[412px] rounded-md border border-gray-600 bg-gray-800">
            <div className="flex h-12 items-center justify-between gap-2 bg-gray-700 border-b border-gray-600 px-4">
                <Heading type="h6" className="text-gray-100 font-semibold">Broker Price Hit</Heading>
                <div className="flex items-center">
                    <ChevronLeft
                        size={24}
                        className="cursor-pointer p-1 text-gray-100"
                        onClick={() => step(-1)}
                    />
                    <Text variant="bodySmall" className="text-basic-100 font-lato tracking-widest">
                        {`${Math.min(index, hits.length - 1) + 1}/${hits.length}`}
                    </Text>
                    <ChevronRight
                        size={24}
                        className="cursor-pointer p-1 text-gray-100"
                        onClick={() => step(1)}
                    />
                </div>
            </div>

            <div className="px-6 py-4">
                <div className="flex items-center gap-2">
                    <Text variant="bodySmall" className="min-w-0 truncate text-gray-100" fontWeight="font-medium">
                        {hit.curveName || DASH}
                    </Text>
                    <span className={'shrink-0 rounded border border-primary-100/40 bg-primary-100/10'
                        + ' px-2 py-0.5 text-xs font-medium text-primary-100'}>
                        self
                    </span>
                </div>

                <ApprovalGrid data={rowsFor(hit)} />

                <div className="border-t border-gray-600 pt-3">
                    <Button
                        type="ghost"
                        buttonTheme="gray"
                        label="Dismiss"
                        size="small"
                        fullWidth
                        className="focus:!bg-transparent"
                        onClick={() => onDismiss(hit)}
                    />
                </div>
            </div>
        </div>
    );
};

BrokerPriceHitDialog.propTypes = {
    hits: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        curveName: PropTypes.string,
        start: PropTypes.string,
        tenor: PropTypes.string,
        price: PropTypes.number,
        size: PropTypes.number,
        buyer: PropTypes.shape({ name: PropTypes.string, company: PropTypes.string }),
        seller: PropTypes.shape({ name: PropTypes.string, company: PropTypes.string })
    })),
    onDismiss: PropTypes.func.isRequired
};

export default BrokerPriceHitDialog;
